import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

const LOGO_URL = "/images/logo.png";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const handleNavClick = () => {
    setIsOpen(false);
    setAboutOpen(false);
    setMoreOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const mainNavLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Pricing', path: '/service-offer-plan' },
    { name: 'How It Works', path: '/how-it-works' },
    { name: 'Gallery', path: '/gallery' },
  ];

  const aboutDropdownLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Founder\'s Desk', path: '/ceo-desk' },
    { name: 'Vision & Mission', path: '/vision-mission' },
    { name: 'Why Choose Us', path: '/why-choose' },
    { name: 'Who We Serve', path: '/who-we-serve' },
  ];

  const moreDropdownLinks = [
    { name: 'Contact', path: '/contact' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Careers', path: '/careers' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" onClick={handleNavClick} className="flex items-center" data-testid="navbar-logo">
            <img 
              src={LOGO_URL} 
              alt="Washora Premium Care" 
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {mainNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={handleNavClick}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  isActive(link.path)
                    ? 'text-[#1E88E5] bg-blue-50'
                    : 'text-[#0B2C5D] hover:text-[#1E88E5] hover:bg-blue-50'
                }`}
              >
                {link.name}
              </Link>
            ))}

            {/* About Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger 
                className="flex items-center px-4 py-2 rounded-lg font-medium text-[#0B2C5D] hover:text-[#1E88E5] hover:bg-blue-50 transition-all"
                data-testid="nav-about-dropdown"
              >
                About <ChevronDown className="ml-1 h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-slate-100 shadow-lg rounded-xl p-2">
                {aboutDropdownLinks.map((link) => (
                  <DropdownMenuItem key={link.path} asChild>
                    <Link 
                      to={link.path}
                      onClick={handleNavClick}
                      className="w-full px-4 py-2 text-[#0B2C5D] hover:text-[#1E88E5] hover:bg-blue-50 rounded-lg cursor-pointer"
                      data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* More Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger 
                className="flex items-center px-4 py-2 rounded-lg font-medium text-[#0B2C5D] hover:text-[#1E88E5] hover:bg-blue-50 transition-all"
                data-testid="nav-more-dropdown"
              >
                More <ChevronDown className="ml-1 h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-slate-100 shadow-lg rounded-xl p-2">
                {moreDropdownLinks.map((link) => (
                  <DropdownMenuItem key={link.path} asChild>
                    <Link 
                      to={link.path}
                      onClick={handleNavClick}
                      className="w-full px-4 py-2 text-[#0B2C5D] hover:text-[#1E88E5] hover:bg-blue-50 rounded-lg cursor-pointer"
                      data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="tel:9044499957"
              className="flex items-center text-[#0B2C5D] hover:text-[#1E88E5] font-medium transition-colors"
              data-testid="nav-phone"
            >
              <Phone className="h-4 w-4 mr-2" />
              9044499957
            </a>
            <Link
              to="/booking"
              onClick={handleNavClick}
              className="bg-[#F7931E] text-white px-6 py-2.5 rounded-full font-semibold hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg hover:shadow-orange-500/30"
              data-testid="nav-book-now"
            >
              Book Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg text-[#0B2C5D] hover:bg-blue-50 transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-slate-100 max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col space-y-1">
              {mainNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={handleNavClick}
                  className={`px-4 py-3 rounded-lg font-medium transition-all ${
                    isActive(link.path)
                      ? 'text-[#1E88E5] bg-blue-50'
                      : 'text-[#0B2C5D] hover:text-[#1E88E5] hover:bg-blue-50'
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile About Dropdown */}
              <Collapsible open={aboutOpen} onOpenChange={setAboutOpen}>
                <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 rounded-lg font-medium text-[#0B2C5D] hover:bg-blue-50 transition-all">
                  <span>About</span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${aboutOpen ? 'rotate-180' : ''}`} />
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 space-y-1 mt-1">
                  {aboutDropdownLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={handleNavClick}
                      className={`block px-4 py-2.5 rounded-lg transition-all ${
                        isActive(link.path)
                          ? 'text-[#1E88E5] bg-blue-50'
                          : 'text-[#64748B] hover:text-[#1E88E5] hover:bg-blue-50'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </CollapsibleContent>
              </Collapsible>

              {/* Mobile More Dropdown */}
              <Collapsible open={moreOpen} onOpenChange={setMoreOpen}>
                <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 rounded-lg font-medium text-[#0B2C5D] hover:bg-blue-50 transition-all">
                  <span>More</span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 space-y-1 mt-1">
                  {moreDropdownLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={handleNavClick}
                      className={`block px-4 py-2.5 rounded-lg transition-all ${
                        isActive(link.path)
                          ? 'text-[#1E88E5] bg-blue-50'
                          : 'text-[#64748B] hover:text-[#1E88E5] hover:bg-blue-50'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </CollapsibleContent>
              </Collapsible>

              <div className="pt-4 px-4 space-y-3 border-t border-slate-100 mt-2">
                <a
                  href="tel:9044499957"
                  className="flex items-center justify-center text-[#0B2C5D] font-medium py-3 border border-[#0B2C5D] rounded-full hover:bg-[#0B2C5D] hover:text-white transition-all"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  9044499957
                </a>
                <Link
                  to="/booking"
                  onClick={handleNavClick}
                  className="block text-center bg-[#F7931E] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#E08215] transition-all"
                >
                  Book Now
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
